import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCircleCheck,
  faCircleXmark,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons'

export function FindingLine({ finding }) {
  return (
    <p className="is-size-7 mb-1">
      <span
        className={`tag lox-no-border mr-1 is-light ${
          finding.severity === 'error' ? 'is-danger' : 'is-warning'
        }`}
      >
        {finding.code}
      </span>
      {finding.operation_sequence ? (
        <span className="has-text-grey mr-1">
          op {finding.operation_sequence}
        </span>
      ) : null}
      {finding.message}
    </p>
  )
}

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`

export function ValidationPills({ validation }) {
  const errorCount = validation.errors.length
  const warningCount = validation.warnings.length
  return (
    <div className="lox-cr-review__pills">
      <span
        className={`lox-cr-review__pill is-error ${
          errorCount === 0 ? 'is-clear' : ''
        }`}
        data-testid="cr-validation-errors"
      >
        <FontAwesomeIcon icon={faCircleXmark} />
        {plural(errorCount, 'error')}
      </span>
      <span
        className={`lox-cr-review__pill is-warning ${
          warningCount === 0 ? 'is-clear' : ''
        }`}
        data-testid="cr-validation-warnings"
      >
        <FontAwesomeIcon icon={faTriangleExclamation} />
        {plural(warningCount, 'warning')}
      </span>
    </div>
  )
}

function findingSubject(finding) {
  if (finding.block_id !== null && finding.block_id !== undefined) {
    return `Block #${finding.block_id}`
  }
  if (finding.treatment_id !== null && finding.treatment_id !== undefined) {
    return `Treatment ${finding.treatment_id}`
  }
  return null
}

function FindingItem({ finding }) {
  const isError = finding.severity === 'error'
  const subject = findingSubject(finding)
  return (
    <div
      className={`lox-cr-review__finding ${
        isError ? 'is-error' : 'is-warning'
      }`}
      data-testid="cr-finding"
    >
      <FontAwesomeIcon
        icon={isError ? faCircleXmark : faTriangleExclamation}
        className="lox-cr-review__finding-icon"
      />
      <div>
        <p className="lox-cr-review__finding-head">
          <span className="lox-cr-review__finding-code">{finding.code}</span>
          {finding.operation_sequence ? (
            <span className="has-text-grey">
              op {finding.operation_sequence}
            </span>
          ) : null}
          {subject && <span>{subject}</span>}
        </p>
        <p className="lox-cr-review__finding-message">{finding.message}</p>
      </div>
    </div>
  )
}

export const NOT_VALIDATED_MESSAGE =
  'Not validated: closed requests are not checked against the live tables.'

// A closed request comes back with empty validation, which is not the same as a clean one.
export default function ChangeRequestValidation({
  validation,
  isValidated = true,
}) {
  const findings = [...validation.errors, ...validation.warnings]

  return (
    <div className="lox-cr-review__findings" data-testid="cr-validation">
      {!isValidated && (
        <p className="lox-cr-review__empty has-text-grey">
          {NOT_VALIDATED_MESSAGE}
        </p>
      )}
      {isValidated && findings.length === 0 && (
        <p className="lox-cr-review__empty">
          <FontAwesomeIcon icon={faCircleCheck} className="has-text-success" />
          No errors or warnings.
        </p>
      )}
      {isValidated &&
        findings.map((finding, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <FindingItem key={`${finding.code}-${index}`} finding={finding} />
        ))}
    </div>
  )
}
